import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/util";
import { Loader2, ChevronDown } from "lucide-react";

/**
 * Dropdown de objetivos poblado automáticamente desde los assets registrados
 * del cliente. Sustituye el input de texto libre en todos los modales de
 * configuración de escaneo.
 *
 * Props:
 *   - clientId       : id del cliente seleccionado (obligatorio)
 *   - value          : valor actual (string). Vacío = ninguno. "all" = todos.
 *   - onChange       : (newValue, targetsForFanOut) => void
 *   - targetType     : filtro por tipo del scanner: "any" | "domain_or_host"
 *                      | "domain_only" | "ip" (default "any")
 *   - allowAll       : boolean (default true) — muestra "Todos los activos"
 *   - testId         : sufijo para data-testid (default "target-picker")
 *   - onTargetsLoaded: (targets[]) => void — opcional, útil para preseleccionar
 */
export default function AssetTargetPicker({
  clientId, value, onChange,
  targetType = "any", allowAll = true,
  testId = "target-picker", onTargetsLoaded,
}) {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!clientId) { setTargets([]); return; }
    let cancelled = false;
    setLoading(true); setErr("");
    api.get(`/clients/${clientId}/scan-targets`)
       .then(r => {
         if (cancelled) return;
         setTargets(r.data.targets || []);
         onTargetsLoaded && onTargetsLoaded(r.data.targets || []);
       })
       .catch(ex => {
         if (cancelled) return;
         setErr(ex?.response?.data?.detail || "No se pudieron cargar los activos");
         setTargets([]);
       })
       .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const filtered = targets.filter(t => {
    if (targetType === "ip") return t.kind === "ip_range";
    if (targetType === "domain_only") return t.kind === "domain";
    if (targetType === "domain_or_host") {
      return t.kind === "domain" || t.kind === "subdomain" || t.kind === "ip_range";
    }
    return true;
  });

  const handleChange = (newVal) => {
    if (newVal === "all") {
      onChange("all", filtered);
    } else {
      onChange(newVal, filtered);
    }
  };

  return (
    <div className="relative">
      <select
        value={value || ""}
        onChange={(e) => handleChange(e.target.value)}
        disabled={!clientId || loading}
        data-testid={testId}
        className={cn(
          "w-full bg-background border border-border px-3 py-2 pr-8 text-sm font-mono",
          "focus:border-primary outline-none appearance-none cursor-pointer",
          (!clientId || loading) && "opacity-50 cursor-not-allowed",
        )}
      >
        <option value="">
          {!clientId ? "-- Selecciona un cliente primero --"
                     : loading ? "Cargando activos…"
                     : filtered.length === 0 ? "-- Sin activos registrados --"
                     : "-- Selecciona un objetivo --"}
        </option>
        {allowAll && filtered.length >= 1 && (
          <option value="all" data-testid={`${testId}-all-option`}>
            ★ Todos los activos ({filtered.length})
          </option>
        )}
        {filtered.map(t => (
          <option key={`${t.kind}:${t.value}`} value={t.value}>
            {t.value}  ({_kindLabel(t.kind)})
          </option>
        ))}
      </select>
      {loading
        ? <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-muted-foreground pointer-events-none" />
        : <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />}
      {err && (
        <div className="mt-1 text-[10px] font-mono text-sev-critical"
             data-testid={`${testId}-error`}>{err}</div>
      )}
      {clientId && !loading && filtered.length === 0 && !err && (
        <div className="mt-1 text-[10px] font-mono text-muted-foreground">
          Este cliente no tiene activos registrados. Agrega dominios o IPs en
          la ficha del cliente antes de escanear.
        </div>
      )}
    </div>
  );
}

function _kindLabel(kind) {
  return {
    domain: "dominio raíz",
    subdomain: "subdominio",
    ip_range: "IP / rango",
  }[kind] || kind;
}
